// Per-table column cache for the MSSQL native resolver — a FALLBACK for
// relations the bus snapshot does not describe (trigger host tables, DDL
// reference targets, cross-database references), not the ordinary dotted-
// access path.
//
// The header here used to read "the bus snapshot for MSSQL today carries
// `columns: []` per table … so the resolver introspects columns lazily the
// first time a relation is bound at the cursor." That stopped being true
// when `ensureMssqlTables` started fetching columns and folding them into
// each `MssqlTableEntry` via `mergeTableIntrospection` — the snapshot now
// carries real columns, and `alias.` on a snapshot table is answered from
// memory with no query at all. The stale comment is worth naming because it
// caused a review to conclude that this file's 60s TTL put a network round-
// trip in front of the suggest widget once a minute; measurement says
// otherwise (`features/perf/mssqlDottedLatency.perf.spec.ts` asserts zero
// `INFORMATION_SCHEMA.COLUMNS` queries during a dotted access, cold cache
// included).
//
// Lookup is keyed by `${connectionId}|${database}|${schema}|${table}` —
// invalidated together with the bus on DDL via `dropMssqlColumnCache`.
// Pure data store — the renderer's `window.electronAPI.executeQueryV2`
// is the only outbound dependency, mirroring `mssqlTableCache.ts`.

const TTL_MS = 60_000;

// INT-4: per-connection invalidation generation. Bumped by
// dropMssqlColumnCache; captured at fetch start; an in-flight fetch whose
// epoch has advanced (a DDL invalidation landed mid-fetch) must NOT write its
// now-stale columns back into the cache. Keyed by connectionId — the same
// granularity as dropMssqlColumnCache — so a drop on connection B cannot
// silently discard an in-flight fetch belonging to connection A.
const epochs = new Map<string, number>();
function epochOf(connectionId: string): number {
  return epochs.get(connectionId) ?? 0;
}

export type MssqlColumnEntry = {
  readonly name: string;
  readonly dataType: string;
  readonly nullable: boolean;
  readonly ordinal: number;
  /** `COLUMNPROPERTY(…, 'IsIdentity')` — see `ColumnMeta.isIdentity`.
   *  Optional: seeded test fixtures and pre-existing cache rows omit it. */
  readonly isIdentity?: boolean;
  /** `COLUMNPROPERTY(…, 'IsComputed')` — see `ColumnMeta.isComputed`. */
  readonly isComputed?: boolean;
};

type CacheRow = {
  columns: readonly MssqlColumnEntry[];
  builtAt: number;
  fetchInFlight?: Promise<readonly MssqlColumnEntry[]>;
};

const cache = new Map<string, CacheRow>();

function keyFor(connectionId: string, database: string, schema: string, table: string): string {
  return `${connectionId}|${database}|${schema.toLowerCase()}|${table.toLowerCase()}`;
}

export function getCachedMssqlColumns(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
): readonly MssqlColumnEntry[] | undefined {
  const row = cache.get(keyFor(connectionId, database, schema, table));
  if (!row) return undefined;
  if (Date.now() - row.builtAt > TTL_MS) return undefined;
  return row.columns;
}

export function dropMssqlColumnCache(connectionId: string): void {
  const prefix = `${connectionId}|`;
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
  // Bump the epoch unconditionally so any in-flight fetch for this connection
  // sees a stale epoch and skips its cache write — preventing resurrection of
  // DDL-invalidated schema after the drop races with a pending fetch.
  epochs.set(connectionId, epochOf(connectionId) + 1);
}

export function __resetMssqlColumnCacheForTest(): void {
  cache.clear();
  epochs.clear();
}

/**
 * Test seam — seed columns synchronously without standing up an electron
 * bridge. Mirrors the `injectPgSchemaForTest` shape and the bus's
 * `injectSnapshotForTest` shape. Gated on test mode.
 */
export function injectMssqlColumnsForTest(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  columns: readonly MssqlColumnEntry[],
): void {
  if (typeof process !== 'undefined') {
    const isTest = process.env?.SQL_STUDIO_TEST === '1' || process.env?.NODE_ENV === 'test';
    if (!isTest) {
      throw new Error(
        'injectMssqlColumnsForTest is only available in test mode (SQL_STUDIO_TEST=1 or NODE_ENV=test).',
      );
    }
  }
  cache.set(keyFor(connectionId, database, schema, table), {
    columns,
    builtAt: Date.now(),
  });
}

async function fetchColumns(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
): Promise<readonly MssqlColumnEntry[]> {
  if (!window.electronAPI?.executeQueryV2) return [];

  // INFORMATION_SCHEMA.COLUMNS query bound to the current database. We
  // qualify the table on schema + name; both are user-supplied identifiers
  // so we escape `'` to defend against pathological table names.
  const escSchema = schema.replace(/'/g, "''");
  const escTable = table.replace(/'/g, "''");
  // `COLUMNPROPERTY` rather than a `sys.columns` join: this query is already
  // scoped to ONE table, so the two scalar calls cost one name resolution
  // each — the bulk path (`mssqlColumnIntrospection.ts`) is the one that has
  // to stay set-based.
  const sql = `SELECT COLUMN_NAME, DATA_TYPE, IS_NULLABLE, ORDINAL_POSITION,
  COLUMNPROPERTY(OBJECT_ID(QUOTENAME(TABLE_SCHEMA) + '.' + QUOTENAME(TABLE_NAME)), COLUMN_NAME, 'IsIdentity') AS IS_IDENTITY,
  COLUMNPROPERTY(OBJECT_ID(QUOTENAME(TABLE_SCHEMA) + '.' + QUOTENAME(TABLE_NAME)), COLUMN_NAME, 'IsComputed') AS IS_COMPUTED
FROM INFORMATION_SCHEMA.COLUMNS
WHERE TABLE_SCHEMA = '${escSchema}' AND TABLE_NAME = '${escTable}'
ORDER BY ORDINAL_POSITION`;

  const result = await window.electronAPI.executeQueryV2({
    connectionId,
    database,
    sql,
    executionId: `mssql-cols-${connectionId}-${database}-${schema}-${table}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    options: { maxRows: 5000 },
  });

  if (!result.success) return [];

  const rows = result.resultSets?.[0]?.rows ?? [];
  const out: MssqlColumnEntry[] = [];
  for (const row of rows) {
    const name = String(row.COLUMN_NAME ?? '').trim();
    if (!name) continue;
    const dataType = String(row.DATA_TYPE ?? '').trim();
    const nullable = String(row.IS_NULLABLE ?? '').toUpperCase() !== 'NO';
    const ordinal = Number(row.ORDINAL_POSITION ?? 0);
    // `COLUMNPROPERTY` returns 1 / 0, or NULL when the object could not be
    // resolved (a #temp table's INFORMATION_SCHEMA row, a dropped object) —
    // NULL folds to false, i.e. "no reason to demote".
    const isIdentity = Number(row.IS_IDENTITY ?? 0) === 1;
    const isComputed = Number(row.IS_COMPUTED ?? 0) === 1;
    out.push({
      name, dataType, nullable, ordinal,
      ...(isIdentity ? { isIdentity: true } : {}),
      ...(isComputed ? { isComputed: true } : {}),
    });
  }
  return out;
}

/**
 * Lazy column fetch. First call kicks off the introspection; subsequent
 * concurrent calls share the in-flight promise. Caller treats this as
 * fire-and-forget on cold cache and re-renders when the snapshot lands —
 * the orchestrator returns whatever's available synchronously and Monaco
 * naturally re-fetches on the next keystroke.
 */
export async function ensureMssqlColumns(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
): Promise<readonly MssqlColumnEntry[]> {
  const key = keyFor(connectionId, database, schema, table);
  const existing = cache.get(key);
  if (existing && !existing.fetchInFlight && Date.now() - existing.builtAt < TTL_MS) {
    return existing.columns;
  }
  if (existing?.fetchInFlight) {
    return existing.fetchInFlight;
  }

  // Capture the epoch before the async gap so any intervening drop that bumps
  // the epoch causes the callbacks below to skip writing stale columns back.
  const startEpoch = epochOf(connectionId);

  const promise = fetchColumns(connectionId, database, schema, table)
    .then((columns) => {
      // Guard: if the epoch advanced (DDL invalidation raced this fetch),
      // discard the pre-DDL result rather than resurrecting the dead entry.
      if (epochOf(connectionId) === startEpoch) {
        cache.set(key, { columns, builtAt: Date.now() });
      }
      return columns;
    })
    .catch(() => {
      // Failure is recorded as an empty list with a fresh timestamp so the
      // resolver doesn't hammer SSTS retrying on every keystroke.
      // Same epoch guard — a mid-flight drop must not plant a stale sentinel.
      if (epochOf(connectionId) === startEpoch) {
        cache.set(key, { columns: [], builtAt: Date.now() });
      }
      return [];
    });

  cache.set(key, {
    columns: existing?.columns ?? [],
    builtAt: existing?.builtAt ?? 0,
    fetchInFlight: promise,
  });
  return promise;
}
